import React from 'react';
import { useParams } from 'react-router-dom';
import { Globe, Tag, Info, Database, Search, Shield, Radio, Clock, ExternalLink, Copy, ChevronRight, Maximize2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/Badge';
import { AssetDetailTabs } from '../../components/assets/AssetDetailTabs';
import { AssetGraph } from '../../components/graphs/AssetGraph';

import { AssetKeyInfo } from '../../components/assets/detail/AssetKeyInfo';
import { AssetWhois } from '../../components/assets/detail/AssetWhois';
import { AssetDNSRecords } from '../../components/assets/detail/AssetDNSRecords';
import { AssetServices } from '../../components/assets/detail/AssetServices';
import { AssetTLSCertificate } from '../../components/assets/detail/AssetTLSCertificate';
import { AssetRecentFindings } from '../../components/assets/detail/AssetRecentFindings';
import { AssetPorts } from '../../components/assets/detail/AssetPorts';
import { AssetFindings } from '../../components/assets/detail/AssetFindings';
import { AssetEvents } from '../../components/assets/detail/AssetEvents';
import { AssetNotes } from '../../components/assets/detail/AssetNotes';
import { AssetRiskScoreCard } from '../../components/assets/detail/AssetRiskScoreCard';

// Sample asset data
const assetData = {
  id: '1',
  name: 'api.example.com',
  type: 'subdomain',
  status: 'active',
  tags: ['production', 'api', 'critical'],
  createdAt: '2024-01-15T10:00:00Z',
  lastScan: '2024-02-28T15:30:00Z',
  riskScore: 'F',
  findings: {
    total: 34,
    open: 12,
    critical: 2,
    high: 4
  },
  keyInfo: {
    parentDomain: 'example.com',
    ipAddress: '192.168.1.100',
    hosting: 'AWS',
    region: 'us-east-1',
    technology: 'Node.js',
    framework: 'Express',
    webServer: 'Nginx'
  },
  whois: {
    registrar: 'GoDaddy.com, LLC',
    registeredOn: '2020-01-01',
    expiresOn: '2025-01-01',
    organization: 'Example Corp',
    contact: {
      name: 'Domain Admin',
      email: 'admin@example.com',
      phone: '+1.1234567890'
    }
  },
  dnsRecords: [
    {
      type: 'A',
      name: 'api.example.com',
      value: '192.168.1.100',
      ttl: 300
    },
    {
      type: 'CNAME',
      name: 'www.api.example.com',
      value: 'api.example.com',
      ttl: 300
    },
    {
      type: 'MX',
      name: 'api.example.com',
      value: 'mail.example.com',
      priority: 10,
      ttl: 3600
    }
  ],
  services: [
    {
      port: 443,
      protocol: 'HTTPS',
      service: 'Web Server',
      version: 'nginx/1.20.1',
      status: 'active'
    },
    {
      port: 80,
      protocol: 'HTTP',
      service: 'Web Server',
      version: 'nginx/1.20.1',
      status: 'redirect'
    }
  ],
  tlsCertificate: {
    subject: 'CN=api.example.com',
    issuer: 'Let\'s Encrypt Authority X3',
    validFrom: '2024-01-01T00:00:00Z',
    validTo: '2024-03-31T23:59:59Z',
    serialNumber: '123456789',
    version: 3,
    algorithm: 'SHA256withRSA',
    sans: ['api.example.com', 'www.api.example.com']
  },
  recentFindings: [
    {
      id: '1',
      severity: 'high',
      title: 'Outdated TLS Configuration',
      description: 'TLS 1.0/1.1 protocols are enabled',
      discoveredAt: '2024-02-27T10:00:00Z',
      status: 'open'
    },
    {
      id: '2',
      severity: 'medium',
      title: 'Missing Security Headers',
      description: 'X-Frame-Options header is not set',
      discoveredAt: '2024-02-26T15:30:00Z',
      status: 'in_progress'
    }
  ]
};

const assetGraphData = {
  nodes: [
    { id: 'asset1', label: assetData.name, type: 'asset', risk: 'medium' },
    { id: 'domain1', label: 'example.com', type: 'domain', risk: 'low' },
    { id: 'ip1', label: '192.168.1.100', type: 'ip', risk: 'medium' },
    { id: 'port1', label: 'Port 443 (HTTPS)', type: 'port', risk: 'low' },
    { id: 'port2', label: 'Port 80 (HTTP)', type: 'port', risk: 'low' },
    { id: 'vuln1', label: 'SQL Injection', type: 'vulnerability', risk: 'critical' },
    { id: 'vuln2', label: 'XSS', type: 'vulnerability', risk: 'high' },
    { id: 'service1', label: 'Web Server (nginx)', type: 'service', risk: 'low' },
    { id: 'cert1', label: 'SSL Certificate', type: 'certificate', risk: 'low' }
  ],
  edges: [
    { source: 'domain1', target: 'asset1', type: 'resolves_to' },
    { source: 'asset1', target: 'ip1', type: 'hosted_on' },
    { source: 'ip1', target: 'port1', type: 'exposes' },
    { source: 'ip1', target: 'port2', type: 'exposes' },
    { source: 'port1', target: 'service1', type: 'runs' },
    { source: 'service1', target: 'vuln1', type: 'vulnerable_to' },
    { source: 'service1', target: 'vuln2', type: 'vulnerable_to' },
    { source: 'port1', target: 'cert1', type: 'uses' }
  ]
};

const AssetDetail = () => {
  const { id } = useParams();
  const [activeTab, setActiveTab] = React.useState('overview');
  const [currentPage, setCurrentPage] = React.useState(1);
  const [filters, setFilters] = React.useState({
    search: '',
    severity: 'all',
    status: 'all',
    timeframe: 'all'
  });

  // Handle filter changes for findings
  const handleFilterChange = (newFilters: {
    search?: string;
    severity?: string;
    status?: string;
    timeframe?: string;
  }) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    // Reset to first page when filters change
    setCurrentPage(1);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-3 gap-6">
              {/* Main Content - 2 columns */}
              <div className="col-span-2 space-y-6">
                <AssetKeyInfo data={assetData.keyInfo} />
                <AssetDNSRecords records={assetData.dnsRecords} />
                <AssetServices services={assetData.services} />
                <AssetRecentFindings findings={assetData.recentFindings} />
              </div>

              {/* Sidebar - 1 column */}
              <div className="space-y-6">
                <AssetWhois data={assetData.whois} />
                <AssetTLSCertificate certificate={assetData.tlsCertificate} />
              </div>
            </div>
          </div>
        );
      case 'graph':
        return (
          <div className="space-y-6">
            <div className="bg-[#0F1629] rounded-xl border border-gray-800/50">
              {/* Graph Controls */}
              <div className="p-4 border-b border-gray-800">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <Button variant="outline" size="sm">
                      <Maximize2 className="h-4 w-4 mr-2" />
                      Zoom to Fit
                    </Button>
                    <div className="flex items-center space-x-2">
                      <label className="text-sm text-gray-400">Layout:</label>
                      <select className="bg-gray-800/30 border border-gray-800 rounded-lg text-sm text-white px-2 py-1">
                        <option>Force-Directed</option>
                        <option>Hierarchical</option>
                        <option>Circular</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <span className="h-3 w-3 rounded-full bg-red-500" />
                      <span className="text-xs text-gray-400">Critical</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="h-3 w-3 rounded-full bg-orange-500" />
                      <span className="text-xs text-gray-400">High</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="h-3 w-3 rounded-full bg-yellow-500" />
                      <span className="text-xs text-gray-400">Medium</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="h-3 w-3 rounded-full bg-emerald-500" />
                      <span className="text-xs text-gray-400">Low</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Graph Canvas */}
              <div className="p-6">
                <div className="w-full h-[600px] rounded-lg bg-[#0F1629]">
                  <AssetGraph
                    nodes={assetGraphData.nodes}
                    links={assetGraphData.edges}
                    width={800}
                    height={600}
                  />
                </div>
              </div>
            </div>

            {/* Graph Statistics */}
            <div className="grid grid-cols-3 gap-6">
              <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
                <h3 className="text-lg font-semibold text-white mb-4">Graph Statistics</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-gray-800/30 rounded-lg">
                    <span className="text-sm text-gray-400">Total Nodes</span>
                    <span className="text-sm text-white">{assetGraphData.nodes.length}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-800/30 rounded-lg">
                    <span className="text-sm text-gray-400">Total Edges</span>
                    <span className="text-sm text-white">{assetGraphData.edges.length}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-800/30 rounded-lg">
                    <span className="text-sm text-gray-400">Connected Components</span>
                    <span className="text-sm text-white">1</span>
                  </div>
                </div>
              </div>

              <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
                <h3 className="text-lg font-semibold text-white mb-4">Node Distribution</h3>
                <div className="space-y-4">
                  {['asset', 'domain', 'ip', 'port', 'service', 'vulnerability', 'certificate'].map(type => (
                    <div key={type} className="flex items-center justify-between p-3 bg-gray-800/30 rounded-lg">
                      <span className="text-sm text-gray-400 capitalize">{type}s</span>
                      <span className="text-sm text-white">
                        {assetGraphData.nodes.filter(n => n.type === type).length}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
                <h3 className="text-lg font-semibold text-white mb-4">Risk Distribution</h3>
                <div className="space-y-4">
                  {['critical', 'high', 'medium', 'low'].map(risk => (
                    <div key={risk} className="flex items-center justify-between p-3 bg-gray-800/30 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <span className={`h-3 w-3 rounded-full ${
                          risk === 'critical' ? 'bg-red-500' :
                          risk === 'high' ? 'bg-orange-500' :
                          risk === 'medium' ? 'bg-yellow-500' :
                          'bg-emerald-500'
                        }`} />
                        <span className="text-sm text-gray-400 capitalize">{risk}</span>
                      </div>
                      <span className="text-sm text-white">
                        {assetGraphData.nodes.filter(n => n.risk === risk).length}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      case 'ports':
        // Map the services data to match the Port interface
        const mappedPorts = assetData.services.map(service => ({
          id: `${service.port}-${service.protocol}`,
          number: service.port,
          protocol: service.protocol,
          service: service.service,
          product: service.service,
          version: service.version,
          state: 'open' as const,
          last_seen: new Date().toISOString(),
          technologies: []
        }));
        return <AssetPorts ports={mappedPorts} />;
      case 'findings':
        // Create a mock findings array since assetData.findings is an object with statistics, not an array
        const mockFindings = assetData.recentFindings || [
          {
            id: '1',
            title: 'SSL Certificate Expiring Soon',
            description: 'The SSL certificate will expire in less than 30 days',
            severity: 'medium',
            status: 'open',
            cvss: 5.5,
            discoveredAt: '2024-02-25T10:00:00Z',
            lastUpdated: '2024-02-26T15:30:00Z'
          },
          {
            id: '2',
            title: 'Outdated Software Version',
            description: 'The web server is running an outdated version with known vulnerabilities',
            severity: 'high',
            status: 'open',
            cvss: 7.2,
            discoveredAt: '2024-02-24T08:15:00Z',
            lastUpdated: '2024-02-25T09:45:00Z'
          }
        ];
        
        // Create pagination info object
        const findingsPagination = {
          count: mockFindings.length,
          next: null,
          previous: null
        };
        
        return <AssetFindings 
          findings={mockFindings} 
          paginationInfo={findingsPagination} 
          onPageChange={setCurrentPage} 
          currentPage={currentPage} 
          itemsPerPage={10} 
          onFilterChange={handleFilterChange} 
        />;
      case 'events':
        // Create mock entries for the events tab
        const mockEntries = [
          {
            id: '1',
            actorDetails: {
              full_name: 'John Doe'
            },
            verboseDescription: 'Updated asset information',
            timestamp: '2024-03-01T14:30:00Z'
          },
          {
            id: '2',
            actorDetails: {
              full_name: 'Jane Smith'
            },
            verboseDescription: 'Added new tag: production',
            timestamp: '2024-02-28T10:15:00Z'
          }
        ];
        
        return <AssetEvents entries={mockEntries} />;
      case 'notes':
        return <AssetNotes objectId={id || ""} objectType="asset" />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-4">
            <div className="p-3 rounded-lg bg-blue-500/10">
              <Globe className="h-6 w-6 text-blue-500" />
            </div>
            <div>
              <div className="flex items-center space-x-3">
                <h1 className="text-2xl font-bold text-white">{assetData.name}</h1>
                <Badge variant="active">{assetData.status}</Badge>
              </div>
              <div className="flex items-center space-x-4 mt-2">
                <div className="flex items-center space-x-2 text-sm text-gray-400">
                  <Tag className="h-4 w-4" />
                  <div className="flex items-center space-x-2">
                    {assetData.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-0.5 rounded-full bg-gray-800/50 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors cursor-pointer"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-400">
                  <Clock className="h-4 w-4" />
                  <span>Last scan: {new Date(assetData.lastScan).toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button className="p-2 rounded-lg bg-gray-800/50 text-gray-400 hover:text-white hover:bg-gray-800 transition-colors">
              <Copy className="h-4 w-4" />
            </button>
            <a
              href={`https://${assetData.name}`}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 rounded-lg bg-gray-800/50 text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"
            >
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-6 gap-4 mt-6">
          <AssetRiskScoreCard riskScore={assetData.riskScore} findings={assetData.findings} />
          <div className="bg-gray-800/30 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-400">Open Ports</div>
              <Radio className="h-4 w-4 text-emerald-500" />
            </div>
            <div className="text-lg font-medium text-white mt-1">{assetData.services.length}</div>
          </div>
          <div className="bg-gray-800/30 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-400">Active Findings</div>
              <Shield className="h-4 w-4 text-red-500" />
            </div>
            <div className="text-lg font-medium text-white mt-1">{assetData.findings.open}</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <AssetDetailTabs
        activeTab={activeTab}
        onTabChange={setActiveTab}
        assetId={assetData.id}
      />

      {/* Tab Content */}
      {renderTabContent()}
    </div>
  );
};

export default AssetDetail;